import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { FONT_FAMILY_MONO, fontWeights } from '../../theme';

export interface CodeProps {
  children: ReactNode;
}

// An inline code span: a machine value (an id, a reference, a code) quoted inside running prose.
// Deliberately prop-less beyond its children — it takes no `variant`, `color` or `align`, because
// it is not a block of type but an interruption of one, and every one of those props would let a
// caller resize or recolour a fragment mid-sentence.
//
// `variant="inherit"` is the whole point: the span adopts the size and weight of the paragraph it
// sits in, so the same component works inside a `body2` row and a `subtitle2` one without the line
// height jumping. `FONT_FAMILY_MONO` is the token DESIGN.md reserves for ids — which is exactly
// what these spans hold.
export function Code({ children }: CodeProps) {
  return (
    <Typography
      variant="inherit"
      component="code"
      sx={{
        fontFamily: FONT_FAMILY_MONO,
        // Size and family alone still let the value recede into the sentence. The weight is the
        // step that makes it findable at a glance — set explicitly rather than inherited, so it
        // outweighs the prose whether that prose is a 400 or the log registry's 500.
        fontWeight: fontWeights.bold,
        // Roboto Mono reads a touch larger than Lato at the same px; a hair under keeps the
        // baseline of a wrapped sentence even.
        fontSize: '0.95em',
        backgroundColor: 'action.hover',
        borderRadius: 0.5,
        // Horizontal only: vertical padding on an inline box overlaps the line above instead of
        // pushing it away, so it would bleed into the previous row of a wrapped sentence.
        px: 0.5,
        // A 36-char UUID wraps mid-token in this column. Without `clone` the background is one box
        // torn across the break, its radius and padding stranded on the first fragment; with it
        // every fragment closes properly.
        boxDecorationBreak: 'clone',
        WebkitBoxDecorationBreak: 'clone',
      }}
    >
      {children}
    </Typography>
  );
}
